import { lazyWithRetry as lazy } from '@utils/lazyWithRetry';
import { RouteConfig } from '@routes/types';
import {
  rideshareMonitoringRoute,
  rideshareProfileRoute,
  rideshareReportingRoute,
  rideshareUtilityRoutes,
} from './shared/rideshareCommonRoutes';

const HomeWrapper = lazy(() => import('@page/HomeWrapper'));
const PageWrapper = lazy(() => import('@page/PageWrapper'));
const PageNotFound = lazy(() => import('@component/PageNotFound/PageNotFound'));

const PayerInfoLight = lazy(
  () => import('@component/LightVersion/Payer/PayerInfoLight/PayerInfoLight')
);
const PayerFeatures = lazy(
  () => import('@component/LightVersion/Payer/PayerFeatures/PayerFeatures')
);
const PayerServices = lazy(
  () => import('@component/LightVersion/Payer/PayerServices/PayerServicesLight')
);

const PayerSettings = lazy(
  () => import('@component/LightVersion/Payer/PayerSettings/PayerSettings')
);

const MembersInfoLight = lazy(
  () =>
    import('@component/LightVersion/Members/MembersInfoLight/MembersInfoLight')
);
const MemberBenefits = lazy(
  () => import('@component/LightVersion/Members/MemberBenefits/MemberBenefits')
);
const MyRoomPreferences = lazy(
  () => import('@component/Member/MyRoomPreferences/MyRoomPreferences')
);

const ContractorsInfoLight = lazy(
  () =>
    import(
      '@component/LightVersion/Contractors/ContractorsInfoLight/ContractorsInfoLight'
    )
);
const CreateContractorFlow = lazy(
  () => import('@component/CreateContractorFlow/CreateContractorFlow')
);
const ContractorsOperationsLight = lazy(
  () =>
    import(
      '@component/LightVersion/Contractors/ContractorsOperationsLight/ContractorsOperationsLight'
    )
);

const Assignment = lazy(() => import('@component/Assignment/Assignment'));
const AuthContactsLightNew = lazy(
  () =>
    import('@component/LightVersion/AuthContactsLightNew/AuthContactsLightNew')
);
const Monitoring = lazy(() => import('@component/Monitoring/Monitoring'));

const RidesPage = lazy(() => import('@component/RidesPage/RidesPage'));
const RidesDetails = lazy(() => import('@component/Ride/RidesDetails'));
const RideTracking = lazy(() => import('@component/RideTracking/RideTracking'));
const StandingOrder = lazy(
  () => import('@component/Ride/StandingOrder/StandingOrder')
);
const QualityIncidents = lazy(
  () => import('@component/QualityIncidents/QualityIncidents')
);

const Users = lazy(() => import('@component/Users/Users'));
const Permissions = lazy(() => import('@component/Permissions/Permissions'));
const MyRoomAuditLogs = lazy(
  () => import('@component/AuditLog/MyRoomAuditLogs/MyRoomAuditLogs')
);

export const rideshareRoutes: RouteConfig[] = [
  {
    path: '/',
    element: HomeWrapper,
    children: [
      {
        path: 'payors',
        element: PageWrapper,
        children: [
          { path: 'information/:id', element: PayerInfoLight },
          { path: 'features/:id', element: PayerFeatures },
          { path: 'services/:id', element: PayerServices },
          { path: 'settings/:id', element: PayerSettings },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'members',
        element: PageWrapper,
        children: [
          { path: 'information/:id', element: MembersInfoLight },
          { path: 'preferences/:id', element: MyRoomPreferences },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'benefits/:id', element: MemberBenefits },
          { path: 'rides/:id', element: RidesPage },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'administrator',
        element: PageWrapper,
        children: [
          { path: 'users', element: Users },
          { path: 'permissions', element: Permissions },
        ],
      },
      {
        path: 'rides',
        element: PageWrapper,
        children: [
          { path: 'information', element: RidesDetails },
          { path: 'information/:id/*', element: RidesDetails },
          { path: 'standing-order', element: StandingOrder },
          { path: 'standing-order/:id', element: StandingOrder },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'quality-incidents', element: QualityIncidents },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
          // Plain segment id (not encrypted): this is the ride_support_url link.
          { path: 'tracking/:segmentId', element: RideTracking },
        ],
      },
      {
        path: 'contractors',
        element: PageWrapper,
        children: [
          { path: 'create', element: CreateContractorFlow },
          { path: 'information/:id', element: ContractorsInfoLight },
          { path: 'operations/:id', element: ContractorsOperationsLight },
          { path: 'rides/:id', element: RidesPage },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'assignment',
        element: PageWrapper,
        children: [{ path: 'information', element: Assignment }],
      },
      {
        path: 'monitoring',
        element: PageWrapper,
        children: [
          {
            path: '',
            element: Monitoring,
          },
        ],
      },
      {
        path: 'standing-order',
        element: PageWrapper,
        children: [
          { path: 'details', element: StandingOrder },
          { path: 'details/:id', element: StandingOrder },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      rideshareMonitoringRoute,
      rideshareProfileRoute,
      rideshareReportingRoute,
    ],
  },
  ...rideshareUtilityRoutes,
];
